import React from 'react'
import MediaView from "./MediaView"
import { Upload, QualityEnum } from "../../interface/Upload"
import "../../css/Transitions.css"

interface UploadSwitcherProps {
  upload: Upload
  transition: string
  isActive: boolean
  quality: QualityEnum
  animationKey: number
  didLoad: (uploadId: string) => void
}

interface UploadSwitcherState {
  uploadA: Upload,
  uploadB?: Upload,
  incoming: string,
  loaded: boolean
}

/* eslint-disable jsx-a11y/alt-text */
export default class UploadSwitcher extends React.Component<UploadSwitcherProps, UploadSwitcherState> {
  timeoutTimer?: any
  
  static TIMEOUT_SECONDS = 15

  /* 
    The logic to alternate between A and B is to avoid re-rendering the img or video element, 
    resulting in a smoother and more dependable transition across browsers and caching scenarios
  */
  constructor(props: UploadSwitcherProps) {
    super(props)

    this.state = {
      uploadA: props.upload,
      incoming: "a",
      loaded: false
    }
  }

  componentDidUpdate(prevProps: UploadSwitcherProps, _prevState: UploadSwitcherState) {
    if (this.props.animationKey !== prevProps.animationKey) {
      console.log(`AnimationKey changed: ${this.props.animationKey} replaces ${prevProps.animationKey}`)

      // Perform the switch
      let newState
      if (this.state.incoming === "a") {
        newState = {
          uploadA: prevProps.upload,
          uploadB: this.props.upload,
          incoming: "b",
          loaded: false
        }
      } else {
        newState = {
          uploadA: this.props.upload,
          uploadB: prevProps.upload,
          incoming: "a",
          loaded: false
        }
      }

      // Time out and move on if the media hasn't loaded
      clearTimeout(this.timeoutTimer)
      this.timeoutTimer = setTimeout(() => {
        if (this.state.loaded) {
          return
        }
        console.log(`TIMEOUT: Media hadn't loaded after ${UploadSwitcher.TIMEOUT_SECONDS} seconds`)
        this.setLoaded(this.props.upload.id)
      }, UploadSwitcher.TIMEOUT_SECONDS * 1000)

      this.setState({ ...newState })
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timeoutTimer)
  }

  handleMediaViewReady(uploadId: string, debugDescription: string) {
    let incomingUploadId = this.state.incoming === "a" ? this.state.uploadA.id : (this.state.uploadB as Upload).id

    if (uploadId === incomingUploadId) {
      console.log(debugDescription)
      this.setLoaded(uploadId)
    }
  }

  setLoaded(uploadId: string) {
    if (this.state.loaded) {
      return
    }

    clearTimeout(this.timeoutTimer)
    this.setState({
      loaded: true
    }, () => {
      this.props.didLoad(uploadId)
    })
  }

  render() {
    let uploadA = this.state.uploadA
    let uploadB = this.state.uploadB

    let transitionIn = this.props.transition + "In"
    let transitionOut = this.props.transition + "Out"

    let commonProps = {
      quality: this.props.quality,
      style: { animationPlayState: this.state.loaded ? "running" : "paused" },
      isReadyCallback: (uploadId: string, debugDescription: string) => this.handleMediaViewReady(uploadId, debugDescription)
    }

    let zIndex = 50
    if (this.props.isActive) {
      if (this.props.transition === "slide" ) {
        zIndex = 25
      } else {
        zIndex = 100
      }
    }

    return (
      <div className="cell" style={{ zIndex: zIndex }}>
        <MediaView
          key={"a-" + uploadA.id}
          upload={uploadA}
          onScreen={this.state.incoming === "a"}
          className={this.state.incoming === "a" ? transitionIn : transitionOut}
          {...commonProps}
        />
        {uploadB &&
          <MediaView
            key={"b-" + uploadB.id}
            upload={uploadB}
            onScreen={this.state.incoming === "b"}
            className={this.state.incoming === "b" ? transitionIn : transitionOut}
            {...commonProps}
          />
        }
      </div>
    )
  }
}